import { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useMsal } from '@azure/msal-react';
import { getClaim } from 'auth/account';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import {
  CompanySearchDto,
  CompanyIndustryCodeDto,
  OrganisationAssignmentDto,
} from 'models/dtoTypes';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import KosmosScoreUpdateWidget from 'kosmos/components/KosmosScoreUpdateWidget';
import SmartGovernanceWidget from 'kosmos/components/SmartGovernanceWidget';
import { getSustainabilityScoreColor } from 'kosmosConfig';
import { Chip, Stack, Tooltip, Radio } from '@mui/material';
import CurrentUser from 'auth/currentUser';
import { EFeature } from 'auth/Authentication';
import { useTranslation } from 'react-i18next';
import { CompanyDetailedDtoWithSelector } from 'kosmos/models';

const PREFIX = 'KosmosCompanyResultWithSmartGovernance';

const classes = {
  badge: `${PREFIX}-badge`,
  table: `${PREFIX}-table`,
  tableBody: `${PREFIX}-tableBody`,
  tableRow: `${PREFIX}-tableRow`,
  checkBoxCell: `${PREFIX}-checkBoxCell`,
  companyNameCell: `${PREFIX}-companyNameCell`,
  companyName: `${PREFIX}-companyName`,
  companyCode: `${PREFIX}-companyCode`,
  address: `${PREFIX}-address`,
  scoreCell: `${PREFIX}-scoreCell`,
  score: `${PREFIX}-score`,
  resultRow: `${PREFIX}-resultRow`,
  industryCodes: `${PREFIX}-industryCodes`,
  external: `${PREFIX}-external`,
};

const Root = styled('div')(({ theme }) => ({
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',

  [`& .${classes.badge}`]: {
    width: '100%',
    height: 50,
    fontSize: 30,
    paddingLeft: 15,
    lineHeight: '50px',
  },

  [`& .${classes.table}`]: {
    width: '90%',
    padding: 0,
    borderCollapse: 'separate',
    borderSpacing: '0px 20px',
  },

  [`& .${classes.tableBody}`]: {
    padding: theme.spacing(6),
  },

  [`& .${classes.tableRow}`]: {
    marginBottom: theme.spacing(3),
    fontSize: 16,
    display: 'flex',
  },

  [`& .${classes.checkBoxCell}`]: {
    width: '5%',
    '& a': {
      cursor: 'pointer',
    },
  },
  [`& .${classes.companyNameCell}`]: {
    width: '50%',
    '& a': {
      cursor: 'pointer',
    },
  },
  [`& .${classes.companyName}`]: {
    fontSize: 26,
  },

  [`& .${classes.scoreCell}`]: {
    width: 200,
    verticalAlign: 'top',
  },

  [`& .${classes.score}`]: {
    cursor: 'pointer',
    backgroundColor: '#24c8a8',
    color: 'white',
    fontWeight: 'bold',
    width: 150,
    height: 80,
    border: '2px solid #24c8a8',
    padding: 0,
    margin: 'auto',
  },
  [`& .${classes.resultRow}`]: {
    border: 'none',
  },
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    color: 'black',
    fontWeight: 'bold',
    border: 'none',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    border: 'none',
  },
}));

interface Props {
  count?: number;
  companies: CompanyDetailedDtoWithSelector[];
  onSelectCompany?: (id: string) => void;
  onSelectSgCompany?: (id: string) => void;
  onOutOfCredits?: () => void;
  multiSearch?: boolean;
}

interface OnComplete {
  (company: CompanySearchDto): void;
}

interface OnTrigger {
  (id: string): void;
}

interface CompanyItemProps {
  company: CompanySearchDto;
  smartGovernanceAssignment?: OrganisationAssignmentDto;
  isInProgress?: boolean;
  disabled?: boolean;
  onComplete: OnComplete;
  onTrigger: OnTrigger;
  onOutOfCredits?: () => void;
  onSelectCompany?: (id: string) => void;
  onSelectSgCompany?: (id: string) => void;
  multiSearch?: boolean;
  selectedCompany: string;
  setSelectedCompany: (id: string) => void;
  uniqueIdSelector: string;
}

function CompanyItem(props: CompanyItemProps): JSX.Element {
  const history = useHistory();
  const { t } = useTranslation('default');
  const {
    selectedCompany,
    setSelectedCompany,
    onTrigger,
    onComplete,
    onSelectCompany,
    onSelectSgCompany,
    isInProgress = false,
    smartGovernanceAssignment,
    disabled = false, // disable trigger button
    multiSearch, //true for multi search popup look and feel
    uniqueIdSelector,
  } = props;

  const [company, setCompany] = useState<CompanySearchDto>(props.company);
  const [statusInfo, setStatusInfo] = useState(company.statusInfo || 'Request info');

  const handleCompanySelection = (id?: string) => {
    setSelectedCompany(id || '');
    if (onSelectCompany) {
      onSelectCompany(id || '');
    }
  };

  const {
    id,
    name = '',
    scoringState = 'NotStarted',
    address,
    addressInFull,
    companyIndustryCodes,
    registrationNumbers,
  } = company;

  useEffect(() => {
    // company.scoringState:  Pending(not started), InPorgress(started), Error, Active
    // state(statusInfo):     Request info        , Pending,             Error, Activ3
    // NOTE: click trigger button => setStatusInfo('Requesting')
    // Note: get first reply from api(start loading) => setStatusInfo('Pending')
    if (isInProgress) {
      setStatusInfo('Pending');
    } else if (company.scoringState === 'Error') {
      setStatusInfo('Error');
    } else if (company.statusInfo === 'Pending') {
      setStatusInfo('Request info');
    } else if (company.statusInfo === 'Active') {
      setStatusInfo('Active');
    }
  }, [company, isInProgress]);

  const isDissolved = statusInfo === 'Dissolved';

  const score = company.esgScore || 0;
  const generateScoreWidgetStyle = () => {
    if (statusInfo === 'Dissolved') {
      return {
        backgroundColor: '#707071',
        border: '2px solid #707071',
      };
    } else if (statusInfo === 'Request info') {
      return {
        backgroundColor: 'white',
        border: '2px solid #24c8a8',
        color: '#24c8a8',
      };
    } else if (scoringState === 'Completed') {
      const color = getSustainabilityScoreColor(score || 0);
      return {
        border: `2px solid ${color || 'black'}`,
        backgroundColor: color || 'black',
      };
    } else {
      return {};
    }
  };

  const generateFullAddress = () => {
    if (addressInFull) {
      return addressInFull;
    }
    if (!address) {
      return 'Unknown';
    }
    const { addressLine1, addressLine2, city, country, postalCode } = address;
    const AddrComponents = [addressLine1, addressLine2, city, country, postalCode];
    return AddrComponents.filter((c) => c).join(', ');
  };

  const handleComplete = (company: CompanySearchDto) => {
    setCompany(company);
    onComplete(company);
  };

  const generateScoringState = () => {
    if (isInProgress || statusInfo === 'Requesting') {
      return 'InProgress';
    }
    return isDissolved ? '' : scoringState;
  };

  const handleSelectCompany = (id: string) => {
    if (scoringState !== 'Completed' || isDissolved) {
      return;
    }
    if (isInProgress) {
      return;
    }
    if (onSelectCompany) {
      onSelectCompany(id);
    } else {
      history.push(`/reports/${id}`);
    }
  };

  const MAX_CHAR = 32;

  const generateCompanyIndustryCodeList = (codeList: CompanyIndustryCodeDto[]) => {
    const filteredList = codeList.filter((a) => {
      return a.industryCodeType === 'nace2';
    });
    const finalData = filteredList.map((a) => {
      return a.description || '';
    });
    const tooltipData = finalData.join(', ');
    let codeDesc = '';
    if (finalData.length > 0) {
      codeDesc = finalData[0].slice(0, MAX_CHAR) + '...';
    }
    if (finalData.length > 1) {
      codeDesc += ' + ' + (finalData.length - 1).toString() + ' more';
    }
    return (
      <Tooltip title={tooltipData}>
        <Stack direction="row" spacing={1}>
          <Chip label={codeDesc} />
        </Stack>
      </Tooltip>
    );
  };

  const handleSelectSgCompany = (id: string) => {
    if (onSelectSgCompany) {
      onSelectSgCompany(id);
    } else {
      window.open(`/combined-reports/${id}?type=sg`, '_blank');
    }
    return;
  };

  const renderSGScore = () => {
    if (CurrentUser.getInstance().canManageResource(EFeature.smartGovernance)) {
      return (
        <StyledTableCell className={classes.scoreCell} align="center">
          <SmartGovernanceWidget
            kosmosCompanyId={id || ''}
            kosmosCompanyScoringState={company.scoringState}
            smartGovernanceAssignment={smartGovernanceAssignment}
            onClickCompleted={handleSelectSgCompany}
          />
        </StyledTableCell>
      );
    } else {
      return <></>;
    }
  };

  const renderChip = () => {
    const isAdmin = CurrentUser.getInstance().isAdmin();
    const { duns, tradesunAIId, openCorporateId } = company;
    const nameList: JSX.Element[] = [];
    if (duns) {
      nameList.push(<Chip key="duns" label={`DUNS: ${duns}`} color="primary" />);
    }
    if (tradesunAIId) {
      nameList.push(<Chip key="ts" label={`TS: ${tradesunAIId}`} color="success" />);
    }
    if (openCorporateId) {
      nameList.push(<Chip key="oc" label={`OC: ${openCorporateId}`} color="secondary" />);
    }

    if (!nameList) {
      return null;
    }
    return isAdmin ? (
      <Stack direction="row" spacing={1} sx={{ mb: 1, mt: 1 }}>
        {nameList.map((item) => item)}
      </Stack>
    ) : (
      <></>
    );
  };

  return (
    <TableRow className={clsx(classes.resultRow, isDissolved ? 'dissolved' : null)}>
      {multiSearch ? (
        <StyledTableCell className={classes.checkBoxCell} align="left">
          <Radio
            checked={selectedCompany === uniqueIdSelector}
            name="company-selection"
            inputProps={{ 'aria-label': 'Radio button demo' }}
            onClick={() => {
              handleCompanySelection(uniqueIdSelector);
            }}
          />
        </StyledTableCell>
      ) : (
        <></>
      )}
      <StyledTableCell className={classes.companyNameCell} align="left">
        <div>
          {!multiSearch ? (
            <Link color="secondary" underline="hover" onClick={() => handleSelectCompany(id || '')}>
              <Typography component="div" color="secondary" className={classes.companyName}>
                {name}
              </Typography>
            </Link>
          ) : (
            <div className={classes.companyName}>{name}</div>
          )}
          <div className={classes.address}>
            {t('address')}: {generateFullAddress()}
          </div>

          {id !== '00000000-0000-0000-0000-000000000000' ? (
            <div>
              {t('source-company-id')}: {id}
            </div>
          ) : (
            <></>
          )}

          {registrationNumbers && !!registrationNumbers.length && (
            <div>
              {t('external-identifiers')}:<br />
              {registrationNumbers?.map((item) => (
                <div className={classes.external} key={item.identifier}>
                  {item.typeDescription}: {item.identifier} <br />
                </div>
              ))}
            </div>
          )}

          {renderChip()}

          {companyIndustryCodes?.length ? (
            <div className={classes.industryCodes}>
              {generateCompanyIndustryCodeList(companyIndustryCodes)}
            </div>
          ) : null}
        </div>
      </StyledTableCell>
      {!multiSearch ? (
        <StyledTableCell className={classes.scoreCell} align="center">
          <div
            className={classes.score}
            style={generateScoreWidgetStyle()}
            onClick={() => handleSelectCompany(id || '')}
          >
            <KosmosScoreUpdateWidget
              lastUpdate={company.lastUpdate || ''}
              initialScore={score}
              initialState={generateScoringState()} // treat dissolved company scoringState to ''
              company={company}
              disabled={disabled || isDissolved} // max records triggered or is dissolved
              onComplete={handleComplete}
              onStarted={() => setStatusInfo('Requesting')}
              onRollback={() => setStatusInfo('Request info')}
              onTrigger={onTrigger}
              onOutOfCredits={props.onOutOfCredits}
            />
          </div>
          <div>
            <b>{t('status')}:</b>{' '}
          </div>
          <div>{statusInfo || 'Request info'}</div>
        </StyledTableCell>
      ) : (
        <></>
      )}
      {!multiSearch ? renderSGScore() : <></>}
    </TableRow>
  );
}

export default function KosmosCompanyResultWithSmartGovernance(props: Props): JSX.Element {
  const MAX_POLL = 5;
  const { accounts } = useMsal();
  const { count = 0, companies, onSelectCompany, onSelectSgCompany, multiSearch = false } = props;
  const role = getClaim(accounts[0], 'extension_atlasRole').toLowerCase(); // admin can score unlimited
  const { t } = useTranslation('default');
  const triggered: string[] = (companies || [])
    .filter((c) => c.id && c.scoringState === 'InProgress' && c.statusInfo !== 'Dissolved')
    .map((c) => c.id || '');
  // maintain the list of company ids are currently in progress
  const [idlist, setIdlist] = useState<string[]>(triggered);
  const [selectedCompany, setSelectedCompany] = useState<string>('');

  // remove id from trigger list
  // this method is passed into children and will be used in setTimeout,
  // have to use a Ref container to fetch lastest idlist or use setState(prev => next) style;
  const handleCompleteCompany = (company: CompanySearchDto) => {
    setIdlist((prev) => {
      const id = company.id || '';
      const idx = prev.indexOf(id);
      if (idx >= 0) {
        const newList = [...prev];
        newList.splice(idx, 1);
        return newList;
      } else {
        return prev;
      }
    });
  };

  // what ids are triggered
  const handleTrigger = (id: string) => {
    setIdlist((prev) => {
      if (!prev.includes(id)) {
        return [...prev, id];
      }
      return prev;
    });
  };

  return (
    <Root>
      <div className={classes.badge}>
        {count === 1 ? `1 ${t('result')}` : `${count} ${t('results')}`}
      </div>
      <Table className={classes.table} aria-label="company result table">
        <TableHead>
          <TableRow
            sx={{
              '& td, & th': {
                fontSize: 18,
                fontWeight: 'bold',
                pt: 1,
                pb: 1,
              },
            }}
          >
            {multiSearch ? <TableCell align="left">Select</TableCell> : <></>}
            <TableCell align="left">{t('result')}</TableCell>
            {!multiSearch ? <TableCell align="center">{t('esg-score')}</TableCell> : <></>}
            <TableCell align="center">
              {!multiSearch && CurrentUser.getInstance().canManageResource(EFeature.smartGovernance)
                ? t('smart-governance')
                : null}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {companies.map((c, i) => (
            <CompanyItem
              company={c}
              smartGovernanceAssignment={c.smartGovernanceAssignment}
              key={c.id || i}
              selectedCompany={selectedCompany}
              setSelectedCompany={setSelectedCompany}
              isInProgress={Boolean(c.id && idlist.includes(c.id))}
              disabled={role !== 'admin' && idlist.length >= MAX_POLL}
              onComplete={handleCompleteCompany}
              onTrigger={handleTrigger}
              onSelectCompany={onSelectCompany}
              onSelectSgCompany={onSelectSgCompany}
              onOutOfCredits={props.onOutOfCredits}
              multiSearch={multiSearch}
              uniqueIdSelector={c.uniqueIdSelector}
            />
          ))}
        </TableBody>
      </Table>
    </Root>
  );
}
